import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import { api } from "@/lib/apiClient";
import { formatRupiah } from "@/lib/assessmentConfig";
import { JOURNEY_STEPS } from "@/components/brand";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Lock, ArrowRight, CheckCircle2, Pencil, MapPin, Wallet, Clock, Target, ShieldAlert,
  Loader2, Play, Trophy, Lightbulb, ListChecks, BarChart3, Rocket, BookOpen, FileText,
  Eye, Download, AlertTriangle, Sparkles,
} from "lucide-react";

const RES_ICONS = [BookOpen, FileText, Rocket];
const CONF_STYLE = { Tinggi: "bg-brand text-white", Sedang: "bg-brand-amber text-white", Rendah: "bg-slate-300 text-slate-700" };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const j = useJourney();
  const [resources, setResources] = useState([]);

  useEffect(() => {
    api.get("/resources").then(({ data }) => setResources(data || [])).catch(() => setResources([]));
  }, []);

  if (j.loading) {
    return <DashboardLayout><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div></DashboardLayout>;
  }

  const { assessment, profileDone, ideaCount, selectedIdeas, valCompleted, valStatus,
    hasWinner, hasBlueprint, hasLaunch, launchDone, winner, states, resume, stepNumber } = j;

  const locked = !(user?.access_status === "active" || user?.access_status === "admin");

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="animate-fade-up flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-ink" data-testid="dashboard-welcome-header">
            Dashboard IDEbisnis AI
          </h1>
          <p className="mt-2 text-lg font-semibold text-brand" data-testid="dashboard-greeting">
            Selamat datang, {user?.first_name || user?.name?.split(" ")[0] || "Pengguna"}
          </p>
          <p className="mt-1 max-w-2xl text-base text-ink-soft leading-relaxed">
            Lanjutkan perjalanan Anda dari ide sampai rencana eksekusi.
          </p>
        </div>
      </div>

      {/* 1. RESUME CARD / ACTIVATION GATE */}
      {locked ? (
        <section className="mt-6 rounded-2xl border-2 border-brand-amber bg-brand-amber/5 p-6 sm:p-7 animate-fade-up" data-testid="activate-access-card">
          <div className="flex items-center gap-1.5 text-brand-amber"><Lock className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Akses Terkunci</span></div>
          <h2 className="mt-2 font-heading text-xl sm:text-2xl font-extrabold text-ink">Aktifkan Akses IDEbisnis AI</h2>
          <p className="mt-2 max-w-2xl text-base text-ink-soft leading-relaxed">
            Untuk mulai menemukan dan memvalidasi bisnis yang paling cocok dengan kondisi Anda, aktifkan akses IDEbisnis AI.
          </p>
          <p className="mt-3 text-sm text-ink-muted">Setelah pembayaran Anda dikonfirmasi, tim kami akan mengaktifkan akses Anda. Perjalanan bisnis lengkap akan langsung terbuka di sini.</p>
        </section>
      ) : (
        <section className="mt-6 rounded-2xl border-2 border-brand bg-gradient-to-br from-brand/5 to-brand-accent/5 p-5 sm:p-6 animate-fade-up" data-testid="resume-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-brand"><Play className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Lanjutkan dari Terakhir</span></div>
              <h2 className="mt-1 font-heading text-lg sm:text-xl font-bold text-ink">{resume.label}</h2>
              <p className="mt-1 text-sm text-ink-soft">Langkah {stepNumber} dari 7 — perjalanan Anda tersimpan otomatis.</p>
            </div>
            <Button onClick={() => navigate(resume.to)} data-testid="resume-cta" className="h-12 shrink-0 bg-brand hover:bg-brand-hover px-6 text-base">
              {resume.complete ? <Sparkles className="mr-1.5 h-5 w-5" /> : null}{resume.label}<ArrowRight className="ml-1.5 h-5 w-5" />
            </Button>
          </div>
        </section>
      )}

      {/* 2. PROGRESS JOURNEY */}
      <section className="mt-8 rounded-2xl border border-borderc bg-white p-5 sm:p-7" data-testid="progress-journey">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg sm:text-xl font-semibold text-ink">Perjalanan Anda</h2>
          <span className="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand" data-testid="journey-step-count">Langkah {stepNumber} dari 7</span>
        </div>
        <div className="mt-6 space-y-3">
          {JOURNEY_STEPS.map((step, i) => {
            const state = states[i];
            const highlighted = state !== "locked";
            const clickable = !locked && (state === "completed" || state === "active" || state === "ready");
            const labelState = state === "completed" ? "Selesai" : state === "locked" ? "Belum dimulai" : "Sedang berjalan";
            const to = STEP_PATH[i];
            return (
              <div key={step.key} data-testid={`progress-step-${i + 1}`} data-state={state}
                onClick={() => clickable && navigate(i === 0 && profileDone ? "/assessment?edit=1" : to)}
                className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${
                  clickable ? "cursor-pointer hover:bg-brand/10 " : ""}${
                  state === "completed" ? "border-brand bg-brand/5"
                  : state === "locked" ? "border-borderc bg-slate-50"
                  : "border-brand-accent bg-brand/5"}`}>
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  highlighted ? "bg-brand text-white" : "bg-slate-200 text-slate-400"}`}>
                  {state === "completed" ? <CheckCircle2 className="h-5 w-5" /> : highlighted ? i + 1 : <Lock className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-heading font-semibold ${highlighted ? "text-ink" : "text-slate-400"}`}>{i + 1}. {step.title}</span>
                    <Badge className={
                      state === "completed" ? "bg-brand/15 text-brand"
                      : state === "locked" ? "bg-slate-200 text-slate-500"
                      : "bg-brand-accent/15 text-brand-accent"}>{labelState}</Badge>
                  </div>
                  <p className={`mt-1 text-sm ${highlighted ? "text-ink-soft" : "text-slate-400"}`}>{step.desc}</p>
                </div>
                {clickable && <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand" />}
              </div>
            );
          })}
        </div>
      </section>

      {/* 3. PROFILE SUMMARY */}
      {profileDone ? (
        <section className="mt-8 rounded-2xl border border-brand-accent bg-brand/5 p-5 sm:p-7" data-testid="profile-summary">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-2 text-brand">
              <CheckCircle2 className="h-5 w-5" /><h2 className="font-heading text-lg font-semibold">Profil Bisnis Anda</h2>
            </div>
            <Button variant="outline" onClick={() => navigate("/assessment?edit=1")} data-testid="edit-profile-btn" className="border-brand-accent bg-white text-brand hover:bg-brand/5">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit Profil
            </Button>
          </div>
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryItem icon={MapPin} label="Lokasi" value={assessment.business_location || "-"} />
            <SummaryItem icon={Wallet} label="Modal Dapat Digunakan" value={formatRupiah(assessment.deployable_capital) || "-"} />
            <SummaryItem icon={Clock} label="Waktu Tersedia" value={assessment.available_time || "-"} />
            <SummaryItem icon={Target} label="Tujuan Utama" value={assessment.primary_goal || "-"} />
            <SummaryItem icon={ShieldAlert} label="Toleransi Risiko" value={assessment.risk_tolerance || "-"} />
          </div>
          {(valStatus || hasWinner || hasBlueprint || hasLaunch) && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-3" data-testid="profile-change-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-amber" />
              <p className="text-sm text-ink-soft leading-relaxed">Perubahan profil dapat membuat rekomendasi lama tidak lagi sepenuhnya sesuai. Rekomendasi tidak akan diperbarui otomatis tanpa tindakan Anda.</p>
            </div>
          )}
        </section>
      ) : (
        !locked && <EmptyState testid="empty-profile" title="Anda belum mengisi Profil Bisnis."
          desc="Mulai dengan menceritakan modal, kemampuan, waktu, dan kondisi Anda."
          cta="Mulai Profil Bisnis" onClick={() => navigate("/assessment")} icon={Play} />
      )}

      {/* 4. BUSINESS WINNER CARD */}
      {hasWinner ? (
        <section className="mt-8 rounded-2xl border-2 border-brand bg-brand/5 p-5 sm:p-7" data-testid="dashboard-winner-card">
          <div className="flex items-center gap-1.5 text-brand"><Trophy className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Business Winner Anda</span></div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-xl font-extrabold text-ink" data-testid="dashboard-winner-name">{winner.winner_name}</h2>
            {winner.confidence && <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${CONF_STYLE[winner.confidence] || "bg-slate-200"}`}>Keyakinan: {winner.confidence}</span>}
            <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-brand border border-brand/30">Dipilih untuk diuji</span>
          </div>
          {winner.winner_reasons?.[0] && <p className="mt-3 text-sm text-ink-soft leading-relaxed">{winner.winner_reasons[0]}</p>}
          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate("/comparison")} data-testid="winner-see-comparison" className="border-brand/40 bg-white text-brand hover:bg-brand/5">Lihat Perbandingan</Button>
            <Button onClick={() => navigate("/blueprint")} data-testid="winner-see-blueprint" className="bg-brand hover:bg-brand-hover">Lihat Blueprint</Button>
          </div>
        </section>
      ) : (
        !locked && <EmptyState testid="empty-winner" title="Business Winner belum tersedia."
          desc="Business Winner akan tersedia setelah dua ide selesai divalidasi."
          cta={valCompleted ? "Tentukan Business Winner" : "Lanjutkan langkah sebelumnya"}
          onClick={() => navigate(valCompleted ? "/comparison" : resume.to)} icon={Trophy} />
      )}

      {/* 5. RESULT CARDS */}
      <section className="mt-10">
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-ink">Hasil Anda</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="result-cards">
          <ResultCard testid="result-ideas" icon={Lightbulb} title="Ide Bisnis Anda"
            available={ideaCount > 0}
            lines={ideaCount > 0 ? [`${ideaCount} ide dihasilkan`, `${selectedIdeas.length} ide dipilih`] : ["Ide bisnis Anda akan tersedia setelah Profil Bisnis selesai."]}
            btn="Lihat Ide" onClick={() => navigate("/ideas")} disabled={ideaCount === 0} />

          <ResultCard testid="result-validation" icon={ListChecks} title="Hasil Validasi"
            available={selectedIdeas.length > 0}
            lines={selectedIdeas.length > 0
              ? [...selectedIdeas.map((s) => `• ${s.name}`), valCompleted ? "Validasi selesai" : "Validasi berjalan"]
              : ["Validasi tersedia setelah Anda memilih 2 ide."]}
            btn="Lihat Validasi" onClick={() => navigate("/validation")} disabled={selectedIdeas.length === 0} />

          <ResultCard testid="result-blueprint" icon={BarChart3} title="Business Blueprint"
            available={hasBlueprint}
            lines={[hasBlueprint ? "Tersedia" : "Belum tersedia", hasBlueprint ? "" : "Blueprint tersedia setelah Business Winner dipilih."].filter(Boolean)}
            btn="Lihat Blueprint" onClick={() => navigate("/blueprint")} disabled={!hasBlueprint} />

          <ResultCard testid="result-launch" icon={Rocket} title="Rencana 7 Hari"
            available={hasLaunch}
            lines={hasLaunch ? [`${launchDone} dari 7 hari selesai`] : ["Rencana 7 Hari tersedia setelah Blueprint selesai."]}
            btn={hasLaunch ? "Lanjutkan Rencana" : "Lihat"} onClick={() => navigate("/launch")} disabled={!hasBlueprint} />
        </div>
      </section>

      {/* 6. PDF RESOURCES */}
      <section className="mt-10" data-testid="resources-section">
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-ink">Panduan IDEbisnis AI</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resource-cards">
          {resources.map((r, i) => {
            const Icon = RES_ICONS[i] || BookOpen;
            return (
              <div key={r.key} data-testid={`resource-card-${r.key}`} className="flex flex-col rounded-2xl border border-borderc bg-white p-5 sm:p-6 transition-all hover:border-brand-accent hover:shadow-md">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-heading text-base font-semibold text-ink">{r.title}</h3>
                <p className="mt-1.5 flex-1 text-sm text-ink-soft leading-relaxed">{r.description}</p>
                {r.locked ? (
                  <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-brand-amber/10 px-3 py-1 text-xs font-medium text-brand-amber" data-testid={`resource-locked-${r.key}`}><Lock className="h-3.5 w-3.5" /> Aktifkan akses untuk mengunduh</span>
                ) : r.linked ? (
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5 border-borderc" onClick={() => window.open(r.view_url, "_blank")} data-testid={`resource-view-${r.key}`}><Eye className="h-4 w-4" /> Lihat</Button>
                    <Button size="sm" className="flex-1 gap-1.5 bg-brand hover:bg-brand-hover" onClick={() => window.open(r.download_url || r.view_url, "_blank")} data-testid={`resource-download-${r.key}`}><Download className="h-4 w-4" /> Unduh</Button>
                  </div>
                ) : (
                  <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-ink-muted" data-testid={`resource-status-${r.key}`}>Belum ditautkan</span>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}

const STEP_PATH = ["/assessment", "/ideas", "/ideas", "/validation", "/comparison", "/blueprint", "/launch"];

function Badge({ children, className }) {
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${className}`}>{children}</span>;
}

function SummaryItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-borderc bg-white p-4">
      <div className="flex items-center gap-1.5 text-ink-muted"><Icon className="h-4 w-4" /><span className="text-xs">{label}</span></div>
      <div className="mt-1.5 text-sm font-semibold text-ink line-clamp-2">{value}</div>
    </div>
  );
}

function ResultCard({ testid, icon: Icon, title, available, lines, btn, onClick, disabled }) {
  return (
    <div data-testid={testid} className={`flex flex-col rounded-2xl border bg-white p-5 sm:p-6 ${available ? "border-brand-accent" : "border-borderc"}`}>
      <div className="flex items-center justify-between">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${available ? "bg-brand/10 text-brand" : "bg-slate-100 text-ink-muted"}`}><Icon className="h-5 w-5" /></div>
        <Badge className={available ? "bg-brand/15 text-brand" : "bg-slate-100 text-slate-500"}>{available ? "Tersedia" : "Belum tersedia"}</Badge>
      </div>
      <h3 className="mt-4 font-heading text-base font-semibold text-ink">{title}</h3>
      <div className="mt-1.5 flex-1 space-y-0.5 text-sm text-ink-soft leading-relaxed">
        {lines.map((l, i) => <p key={i}>{l}</p>)}
      </div>
      <Button onClick={onClick} disabled={disabled} variant={available ? "default" : "outline"}
        className={`mt-4 w-fit ${available ? "bg-brand hover:bg-brand-hover" : "border-borderc"}`} data-testid={`${testid}-btn`}>
        {btn} <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

function EmptyState({ testid, title, desc, cta, onClick, icon: Icon }) {
  return (
    <section className="mt-8 rounded-2xl border border-dashed border-borderc bg-white p-6 text-center" data-testid={testid}>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-ink-muted"><Icon className="h-6 w-6" /></div>
      <h3 className="mt-4 font-heading text-base font-semibold text-ink">{title}</h3>
      <p className="mt-1.5 text-sm text-ink-soft">{desc}</p>
      <Button onClick={onClick} className="mt-4 bg-brand hover:bg-brand-hover" data-testid={`${testid}-cta`}>{cta} <ArrowRight className="ml-1 h-4 w-4" /></Button>
    </section>
  );
}
